import { signal } from '@preact/signals';
import { createAudio, type AudioModule } from '@/lib/audio';
import { createUiPrefs } from '@/lib/ui-prefs';

let _audio: AudioModule | null = null;

/**
 * Reactive view of the current mute state. Components read `.value` to auto-subscribe;
 * mutate ONLY via `audio().setEnabled(v)` so the signal and prefs stay in lockstep.
 */
export const soundEnabledSignal = signal<boolean>(false);

/** Lazy single audio instance bound to window.localStorage prefs. */
export function audio(): AudioModule {
  if (!_audio) {
    const prefs = createUiPrefs(window.localStorage);
    _audio = createAudio({
      getSoundEnabled: () => prefs.getSoundEnabled(),
      setSoundEnabled: (v) => {
        prefs.setSoundEnabled(v);
        soundEnabledSignal.value = v;
      },
    });
    soundEnabledSignal.value = prefs.getSoundEnabled();
  }
  return _audio;
}

/**
 * Test-only: reset the cached singleton AND the signal so the next audio() call
 * rebuilds with fresh prefs (e.g., after a beforeEach localStorage.clear()).
 * Production code should never call this.
 */
export function __resetAudioSingletonForTests(): void {
  _audio = null;
  soundEnabledSignal.value = false;
}
